import React from 'react';
import ReactDOM from 'react-dom';
import firebase from 'firebase';
import { ThemeProvider } from 'styled-components';
import { BrowserRouter as Router } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import { Provider as ReduxProvider } from 'react-redux';
import { store } from './redux';
import Theme from './theme';
import App from './App';
import './index.css';
import { persistor } from './redux/store';
import { Loader } from './components/atoms';
import { initializeSegment } from './segment';
import { IUserData } from './types';
import { initializeSentry } from './utils/sentry';
import initializePlotline from './utils/integrations/plotline';
import initializeGtag from './utils/integrations/gtag';
import initializeClarity from './utils/integrations/clarity';
import initializeAmplitude, { identifyAmplitudeUser } from './utils/integrations/amplitude';
import { initializeRecaptcha } from './utils/integrations/reCaptcha';

declare global {
    interface Window {
        recaptchaVerifier: firebase.auth.RecaptchaVerifier;
        confirmationResult: firebase.auth.ConfirmationResult | null;
        analytics: any;
        plotline: (...args: (unknown | Record<string, unknown>)[]) => void;
        _plotQueue?: any[];
        dataLayer?: any[];
        fcWidget: {
            init: (config: any) => void;
            open: () => void;
            destroy: () => void;
            isLoaded: () => boolean;
            on: (a: any, b: any) => void;
            user: IUserData;
            // add more methods and properties as needed
        };
        grecaptcha: {
            enterprise: {
                execute: (siteKey: string, options: { action: string }) => Promise<string>;
                ready: (callback: () => void) => void;
            };
        };
    }
}

initializeSentry();
initializeSegment();
initializePlotline();
initializeGtag();
initializeClarity();
initializeRecaptcha();
// Initialize Amplitude with user context once available via Redux.
// We avoid blocking app render; this kicks off script loading immediately.
try {
    const state = store.getState() as any;
    const user = state?.user?.userData?.data;
    const userId = user?.userId as string | undefined;
    initializeAmplitude(userId);
    // Re-identify when auth state changes and user becomes available
    let lastUserId = userId;
    store.subscribe(() => {
        try {
            const s = store.getState() as any;
            const u = s?.user?.userData?.data;
            const nextUserId = u?.userId as string | undefined;
            if (nextUserId && nextUserId !== lastUserId) {
                identifyAmplitudeUser(nextUserId);
                lastUserId = nextUserId;
            }
        } catch {
            // no-op
        }
    });
} catch {
    // no-op
}
ReactDOM.render(
    <ReduxProvider store={store}>
        <ThemeProvider theme={Theme}>
            {/** TODO: Show splash page here while data is being loaded **/}
            <PersistGate loading={<Loader />} persistor={persistor}>
                <Router>
                    <App />
                </Router>
            </PersistGate>
        </ThemeProvider>
    </ReduxProvider>,
    document.getElementById('root')
);
